// import axios, { AxiosError } from "axios"
// import React, { createContext, useCallback, useEffect, useState } from "react"
// import toast from "react-hot-toast"
// import { ErrorType, User } from "../types"

// export interface UserContextType {
//   user: User | null
//   isPending: boolean
//   error: ErrorType | null
//   loginUser: (credentials: {
//     email: string
//     password: string
//   }) => Promise<User | null>
//   fetchUser: () => Promise<void>
//   logoutUser: () => Promise<void>
// }

// export const UserContext = createContext<UserContextType | undefined>(undefined)

// export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
//   children,
// }) => {
//   const apiBaseUrl = import.meta.env.VITE_API_BASE_URL
//   const [user, setUser] = useState<User | null>(null)
//   const [isPending, setisPending] = useState<boolean>(true)
//   const [error, setError] = useState<ErrorType | null>(null)

//   const loginUser = useCallback(
//     async (credentials: {
//       email: string
//       password: string
//     }): Promise<User | null> => {
//       setisPending(true)
//       setError(null)
//       try {
//         const response = await axios.post<{
//           user: User
//           redirect_url?: string
//           message?: string
//         }>(`${apiBaseUrl}/auth/login`, credentials, { withCredentials: true })

//         if (response.data?.redirect_url) {
//           toast(response.data?.message || "")
//           window.location.href = response.data?.redirect_url
//           return null
//         }
//         const user = response.data.user
//         setUser(user)
//         return user
//       } catch (error) {
//         const err = error as AxiosError<{
//           message?: string
//           errors?: Record<string, string[]>
//         }>

//         if (err.response?.data) {
//           toast.error(
//             err?.response?.data?.message || "An unknown error occurred."
//           )
//           const serverError = err.response.data
//           const validationErrors = serverError.errors || {}
//           setError({
//             message: serverError.message || "Validation failed.",
//             errors: validationErrors,
//           })
//         } else {
//           toast.error("An unknown error occurred.")
//           setError({
//             message: "An unknown error occurred.",
//             errors: {},
//           })
//         }
//         return null
//       } finally {
//         setisPending(false)
//       }
//     },
//     [apiBaseUrl]
//   )

//   const fetchUser = useCallback(async () => {
//     try {
//       setisPending(true)
//       setError(null)

//       const response = await axios.get(`${apiBaseUrl}/auth/me`, {
//         withCredentials: true,
//       })
//       setUser(response.data?.user)
//     } catch (error) {
//       console.error(error)
//       const err = error as AxiosError<{ message?: string }>
//       if (err.response?.data) {
//         setError(err.response.data as ErrorType)
//       } else {
//         setError({
//           message: "An unknown error occurred.",
//           errors: {},
//         })
//       }
//     } finally {
//       setisPending(false)
//     }
//   }, [apiBaseUrl])

//   const logoutUser = useCallback(async () => {
//     try {
//       setisPending(true)
//       setError(null)

//       await axios.post(
//         `${apiBaseUrl}/auth/logout`,
//         {},
//         { withCredentials: true }
//       )
//       setUser(null)
//     } catch (error) {
//       console.error(error)
//       const err = error as AxiosError<{ message?: string }>
//       if (err.response?.data) {
//         setError(err.response.data as ErrorType)
//       } else {
//         setError({
//           message: "An unknown error occurred.",
//           errors: {},
//         })
//       }
//     } finally {
//       setisPending(false)
//     }
//   }, [apiBaseUrl])

//   useEffect(() => {
//     fetchUser()
//   }, [fetchUser])

//   return (
//     <UserContext.Provider
//       value={{ user, isPending, error, loginUser, fetchUser, logoutUser }}
//     >
//       {children}
//     </UserContext.Provider>
//   )
// }

import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import React, { createContext, useCallback, useState } from "react";
import toast from "react-hot-toast";
import { ErrorType, User } from "../types";

export interface UserContextType {
  user: User | null;
  isPending: boolean;
  error: ErrorType | null;
  loginUser: (credentials: {
    email: string;
    password: string;
  }) => Promise<User | null>;
  fetchUser: () => Promise<void>;
  logoutUser: () => Promise<void>;
}

export const UserContext = createContext<UserContextType | undefined>(
  undefined,
);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  const queryClient = useQueryClient();

  // State lokal khusus untuk menangani proses login & logout
  const [isActionPending, setIsActionPending] = useState<boolean>(false);
  const [actionError, setActionError] = useState<ErrorType | null>(null);

  // 1. Integrasi useQuery untuk fetchUser
  const {
    data: user = null,
    isFetching: isQueryPending,
    refetch,
  } = useQuery<User | null>({
    queryKey: ["authUser"],
    queryFn: async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/auth/me`, {
          withCredentials: true,
        });
        return response.data?.user || null;
      } catch (error) {
        // Jika error (misal 401 Unauthorized), kembalikan null agar tidak crash
        return null;
      }
    },
    retry: false, // Tidak perlu retry jika belum login
    staleTime: 1000 * 60 * 5, // Cache selama 5 menit
  });

  // Kombinasikan isPending (bernilai true saat fetch awal, login, atau logout)
  const isPending = isQueryPending || isActionPending;
  const error = actionError;

  const fetchUser = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const loginUser = useCallback(
    async (credentials: {
      email: string;
      password: string;
    }): Promise<User | null> => {
      setIsActionPending(true);
      setActionError(null);
      try {
        const response = await axios.post<{
          user: User;
          redirect_url?: string;
          message?: string;
        }>(`${apiBaseUrl}/auth/login`, credentials, { withCredentials: true });

        if (response.data?.redirect_url) {
          toast(response.data?.message || "");
          window.location.href = response.data?.redirect_url;
          return null;
        }

        const loggedInUser = response.data.user;

        // 2. Perbarui cache React Query secara langsung setelah login berhasil
        queryClient.setQueryData(["authUser"], loggedInUser);

        return loggedInUser;
      } catch (error) {
        const err = error as AxiosError<{
          message?: string;
          errors?: Record<string, string[]>;
        }>;

        if (err.response?.data) {
          toast.error(
            err?.response?.data?.message || "An unknown error occurred.",
          );
          const serverError = err.response.data;
          const validationErrors = serverError.errors || {};
          setActionError({
            message: serverError.message || "Validation failed.",
            errors: validationErrors,
          });
        } else {
          toast.error("An unknown error occurred.");
          setActionError({
            message: "An unknown error occurred.",
            errors: {},
          });
        }
        return null;
      } finally {
        setIsActionPending(false);
      }
    },
    [apiBaseUrl, queryClient],
  );

  const logoutUser = useCallback(async () => {
    try {
      setIsActionPending(true);
      setActionError(null);

      await axios.post(
        `${apiBaseUrl}/auth/logout`,
        {},
        { withCredentials: true },
      );

      // 3. Hapus data user dari cache setelah logout
      queryClient.setQueryData(["authUser"], null);

      const params = new URLSearchParams(window.location.search);
      const tag = params.get("tag");

      window.location.href = tag ? `/login?tag=${tag}` : "/login";
    } catch (error) {
      console.error(error);
      const err = error as AxiosError<{ message?: string }>;
      if (err.response?.data) {
        setActionError(err.response.data as ErrorType);
      } else {
        setActionError({
          message: "An unknown error occurred.",
          errors: {},
        });
      }
    } finally {
      setIsActionPending(false);
    }
  }, [apiBaseUrl, queryClient]);

  return (
    <UserContext.Provider
      value={{ user, isPending, error, loginUser, fetchUser, logoutUser }}
    >
      {children}
    </UserContext.Provider>
  );
};
